import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Play, Video } from 'lucide-react';
import { getVideos } from '../lib/api';
import { getVideoEmbed } from '../lib/videoUtils';

const VideoCard = ({ video, variants }) => {
  const [playing, setPlaying] = useState(false);
  const embed = getVideoEmbed(video);

  return (
    <motion.div
      variants={variants}
      whileHover={{ scale: 1.02, y: -8 }}
      className="group relative bg-gradient-to-br from-[#0A1628] to-[#0F1D33] rounded-3xl overflow-hidden border border-white/10 hover:border-[#60A5FA]/50 transition-all duration-300"
    >
      {/* Video Player Area */}
      <div className="relative aspect-video bg-gradient-to-br from-[#1a2332] to-[#0A1628]">
        {playing ? (
          embed.type === 'video' ? (
            <video src={embed.src} controls autoPlay className="w-full h-full object-cover">
              Tu navegador no soporta video.
            </video>
          ) : (
            <iframe
              src={`${embed.src}${embed.src.includes('?') ? '&' : '?'}autoplay=1`}
              title={video.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer"
          >
            {/* Thumbnail if available */}
            {video.thumbnail_url && (
              <img src={video.thumbnail_url} alt={video.title} className="absolute inset-0 w-full h-full object-cover opacity-60" loading="lazy" />
            )}
            <div className="absolute inset-0 flex items-center justify-center opacity-10">
              <Video className="w-32 h-32 text-white" />
            </div>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="relative z-10 w-20 h-20 bg-[#60A5FA] rounded-full flex items-center justify-center shadow-2xl group-hover:shadow-[0_0_40px_rgba(96,165,250,0.5)] transition-all duration-300"
            >
              <Play className="w-10 h-10 text-white ml-1" fill="white" />
            </motion.div>
          </button>
        )}
      </div>

      {/* Info */}
      <div className="p-6 bg-[#0A1628]/80 backdrop-blur-sm">
        <h3 className="text-xl font-bold text-white mb-2">{video.title}</h3>
        {video.description && <p className="text-white/60 text-sm">{video.description}</p>}
      </div>
    </motion.div>
  );
};

const VideoSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setVideos(await getVideos());
      } catch (e) {
        setVideos([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
  };

  // Don't render the section at all if empty and not loading (keeps homepage clean)
  // But show elegant placeholder to indicate coming soon
  return (
    <section className="bg-[#0F1D33] py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#60A5FA] rounded-full filter blur-3xl" />
      </div>

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-7xl mx-auto px-6 relative z-10"
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <span className="text-[#60A5FA] text-lg font-mono mb-4 block">Contenido multimedia</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Nuestros trabajos en <span className="text-[#60A5FA]">video</span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Mira nuestros procesos y resultados en acción
          </p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="aspect-video bg-[#0A1628]/50 rounded-3xl animate-pulse" />
            ))}
          </div>
        ) : videos.length === 0 ? (
          <motion.div
            variants={itemVariants}
            className="bg-gradient-to-br from-[#0A1628] to-[#0F1D33] rounded-3xl border border-white/10 p-12 text-center"
          >
            <div className="w-20 h-20 bg-[#60A5FA] rounded-full flex items-center justify-center mx-auto mb-6 opacity-80">
              <Play className="w-10 h-10 text-white ml-1" fill="white" />
            </div>
            <h3 className="text-white font-semibold text-2xl mb-3">Videos disponibles próximamente</h3>
            <p className="text-white/60 max-w-xl mx-auto">
              Pronto compartiremos videos de nuestros procesos de detailing y resultados.
            </p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} variants={itemVariants} />
            ))}
          </div>
        )}
      </motion.div>
    </section>
  );
};

export default VideoSection;
